import React, { useState } from 'react';
import {
  User,
  Mail,
  Phone,
  Mic,
  CheckCircle,
  AlertCircle,
  Loader,
  X,
  Sparkles,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useCelebration } from '../../contexts/CelebrationContext';
import api from '../../config/api';

const PerformerApplicationForm = ({ logoUrl, onClose }) => {
  const { currentUser } = useAuth();
  const { triggerConfetti } = useCelebration();

  const [formData, setFormData] = useState({
    applicantName: currentUser ? (currentUser.displayName || currentUser.email || '') : '',
    applicantEmail: currentUser ? currentUser.email || '' : '',
    applicantPhone: currentUser ? currentUser.phone || '' : '',
    actName: '',
    performanceType: '',
    genre: '',
    experience: '',
    socialLinks: '',
    website: '',
    availabilityNotes: '',
    notes: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const validate = () => {
    if (!formData.applicantName.trim()) return 'Please enter your name';
    if (!formData.applicantEmail.trim()) return 'Please enter your email';
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.applicantEmail)) return 'Please enter a valid email address';
    if (!formData.actName.trim()) return 'Please enter your act or stage name';
    if (!formData.performanceType.trim()) return 'Please specify your performance type';
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const vErr = validate();
    if (vErr) {
      setError(vErr);
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      let res = await api.post('/performer-applications/applications', formData);
      if (!res.ok && res.status === 404) {
        res = await api.post('/performers/applications', formData);
      }

      if (res.ok) {
        try {
          triggerConfetti();
        } catch (confettiErr) {
          console.debug('Confetti error:', confettiErr);
        }
        setSuccess(true);
      } else {
        let errorMessage = 'Failed to submit application';
        try {
          const data = await res.json();
          errorMessage = data.error || data.message || errorMessage;
        } catch {
          errorMessage = res.statusText || errorMessage;
        }
        throw new Error(errorMessage);
      }
    } catch (err) {
      console.error('Performer application error:', err);
      setError(err.message || 'Failed to submit performer application. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="fixed inset-0 bg-black/70 backdrop-blur-md flex items-center justify-center z-[9999] p-3 sm:p-4 animate-fadeIn" onClick={(e) => e.target === e.currentTarget && onClose()}>
        <div className="bg-white rounded-3xl max-w-sm sm:max-w-md w-full p-6 sm:p-8 text-center shadow-2xl border border-orange-100 my-auto animate-scaleUp relative overflow-hidden">
          {/* Background festive glow */}
          <div className="absolute -top-16 -right-16 w-36 h-36 bg-orange-400/20 rounded-full blur-2xl pointer-events-none" />
          <div className="absolute -bottom-16 -left-16 w-36 h-36 bg-purple-500/20 rounded-full blur-2xl pointer-events-none" />

          {/* Celebration Badge */}
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-orange-100 text-orange-700 text-xs font-bold mb-4 animate-bounce">
            <Sparkles className="w-3.5 h-3.5 text-orange-500" />
            <span>Application Submitted!</span>
          </div>

          {/* Spinning Logo Container with Glow Ring */}
          <div className="relative w-24 h-24 sm:w-28 sm:h-28 mx-auto mb-4 flex items-center justify-center">
            <div className="absolute inset-0 rounded-full bg-gradient-to-tr from-primary-500 via-orange-400 to-yellow-400 animate-spin opacity-75 blur-sm" style={{ animationDuration: '4s' }} />
            <div className="relative w-full h-full rounded-full bg-white p-2 shadow-xl flex items-center justify-center border-2 border-orange-200 animate-logo-celebrate">
              {logoUrl ? (
                <img 
                  src={logoUrl} 
                  alt="BOW Logo" 
                  className="w-full h-full object-contain rounded-full"
                />
              ) : (
                <div className="w-full h-full rounded-full bg-gradient-to-br from-primary-600 to-orange-500 flex items-center justify-center text-white text-2xl font-black">
                  BOW
                </div>
              )}
            </div>
          </div>

          <h3 className="text-xl sm:text-2xl font-extrabold text-gray-900 mb-2">Thank You for Submitting!</h3>
          <p className="text-xs sm:text-sm text-gray-600 mb-6 leading-relaxed">
            Your performer application has been received. Our artistic coordination team will review your details and reach out soon!
          </p>

          <button
            onClick={onClose}
            className="w-full py-3 rounded-xl bg-gradient-to-r from-primary-600 to-orange-600 hover:from-primary-700 hover:to-orange-700 text-white font-bold text-sm shadow-lg shadow-primary-500/25 transition-all duration-200 hover:scale-105"
          >
            Done
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-2.5 sm:p-4 md:p-6 bg-black/60 backdrop-blur-sm overflow-y-auto animate-fadeIn"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="relative w-full max-w-lg sm:max-w-2xl max-h-[85vh] sm:max-h-[90vh] flex flex-col bg-white rounded-2xl sm:rounded-3xl shadow-2xl border border-gray-100 overflow-hidden my-auto animate-scaleUp transform transition-all" role="dialog" aria-modal="true">
        {/* Header */}
        <div className="p-3.5 sm:p-6 border-b border-gray-100 flex items-center justify-between shrink-0 bg-white">
          <div className="flex items-center flex-1 min-w-0 pr-3">
            {logoUrl && (
              <img
                src={logoUrl}
                alt="BOW Logo"
                className="w-8 h-8 sm:w-10 sm:h-10 rounded-full mr-2.5 sm:mr-3 object-contain shrink-0"
              />
            )}
            <div className="min-w-0">
              <h3 className="text-base sm:text-xl md:text-2xl font-bold text-gray-900 tracking-tight truncate">Performer Application</h3>
              <p className="text-[11px] sm:text-sm text-gray-500 mt-0.5 truncate">
                Apply to perform at BOW events
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="shrink-0 p-1.5 sm:p-2 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-500 hover:text-gray-700 transition-colors focus:outline-none"
            aria-label="Close modal"
            type="button"
          >
            <X className="w-4 h-4 sm:w-5 sm:h-5" />
          </button>
        </div>

        {/* Form Body */}
        <form onSubmit={handleSubmit} className="p-3.5 sm:p-6 space-y-2.5 sm:space-y-4 flex-1 overflow-y-auto min-h-0 overscroll-y-contain">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-2.5 sm:p-3 flex items-start">
              <AlertCircle className="w-4 h-4 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
              <div className="min-w-0">
                <h4 className="text-red-800 font-semibold text-xs sm:text-sm">Error</h4>
                <p className="text-red-700 text-[11px] sm:text-xs mt-0.5 break-words">{error}</p>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 sm:gap-3 min-w-0">
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1 flex items-center gap-1.5">
                <User className="w-3.5 h-3.5 text-primary-600" />
                Name *
              </label>
              <input
                type="text"
                name="applicantName"
                value={formData.applicantName}
                onChange={handleChange}
                className="w-full px-3 py-1.5 sm:py-2 text-xs sm:text-sm bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all shadow-sm"
                required
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1 flex items-center gap-1.5">
                <Mail className="w-3.5 h-3.5 text-primary-600" />
                Email *
              </label>
              <input
                type="email"
                name="applicantEmail"
                value={formData.applicantEmail}
                onChange={handleChange}
                className="w-full px-3 py-1.5 sm:py-2 text-xs sm:text-sm bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all shadow-sm"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 sm:gap-3 min-w-0">
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1 flex items-center gap-1.5">
                <Phone className="w-3.5 h-3.5 text-primary-600" />
                Phone
              </label>
              <input
                type="tel"
                name="applicantPhone"
                value={formData.applicantPhone}
                onChange={handleChange}
                className="w-full px-3 py-1.5 sm:py-2 text-xs sm:text-sm bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all shadow-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1 flex items-center gap-1.5">
                <Mic className="w-3.5 h-3.5 text-primary-600" />
                Act / Stage Name *
              </label>
              <input
                type="text"
                name="actName"
                value={formData.actName}
                onChange={handleChange}
                className="w-full px-3 py-1.5 sm:py-2 text-xs sm:text-sm bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all shadow-sm"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 sm:gap-3 min-w-0">
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Performance Type *</label>
              <input
                type="text"
                name="performanceType"
                value={formData.performanceType}
                onChange={handleChange}
                placeholder="Band / Dance / DJ / Solo / etc."
                className="w-full px-3 py-1.5 sm:py-2 text-xs sm:text-sm bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all shadow-sm"
                required
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Genre</label>
              <input
                type="text"
                name="genre"
                value={formData.genre}
                onChange={handleChange}
                className="w-full px-3 py-1.5 sm:py-2 text-xs sm:text-sm bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all shadow-sm"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 sm:gap-3 min-w-0">
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Website</label>
              <input
                type="url"
                name="website"
                value={formData.website}
                onChange={handleChange}
                placeholder="https://..."
                className="w-full px-3 py-1.5 sm:py-2 text-xs sm:text-sm bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all shadow-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Social Links</label>
              <input
                type="text"
                name="socialLinks"
                value={formData.socialLinks}
                onChange={handleChange}
                placeholder="Instagram / YouTube links"
                className="w-full px-3 py-1.5 sm:py-2 text-xs sm:text-sm bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all shadow-sm"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">Experience</label>
            <textarea
              name="experience"
              value={formData.experience}
              onChange={handleChange}
              rows={2}
              className="w-full px-3 py-1.5 sm:py-2 text-xs sm:text-sm bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all shadow-sm resize-none"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">Availability Notes</label>
            <textarea
              name="availabilityNotes"
              value={formData.availabilityNotes}
              onChange={handleChange}
              rows={2}
              className="w-full px-3 py-1.5 sm:py-2 text-xs sm:text-sm bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all shadow-sm resize-none"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">Notes</label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              rows={2}
              className="w-full px-3 py-1.5 sm:py-2 text-xs sm:text-sm bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all shadow-sm resize-none"
            />
          </div>

          {/* Action buttons */}
          <div className="pt-2.5 sm:pt-3 flex items-center justify-end gap-2.5 border-t border-gray-100 shrink-0">
            <button
              type="button"
              onClick={onClose}
              className="px-3.5 py-1.5 sm:py-2 text-xs sm:text-sm font-semibold text-gray-600 hover:text-gray-800 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="inline-flex items-center justify-center gap-1.5 px-5 py-2 sm:py-2.5 rounded-xl bg-primary-600 hover:bg-primary-700 text-white font-bold text-xs sm:text-sm shadow-md transition-all duration-200 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
            >
              {isSubmitting ? (
                <>
                  <Loader className="w-3.5 h-3.5 animate-spin" />
                  <span>Submitting...</span>
                </>
              ) : (
                'Submit Performer Application'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PerformerApplicationForm;
